import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS_LABEL = {
  not_started: "Not started",
  email_verification_required: "Email verification required",
  pending: "Verification pending",
  admin_review_required: "Admin review required",
  approved: "Approved",
  rejected: "Rejected",
  more_info_required: "More information required",
};
const STATUS_CLS = {
  approved: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  rejected: "text-red-400 border-red-500/30 bg-red-500/10",
  admin_review_required: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  more_info_required: "text-amber-400 border-amber-500/30 bg-amber-500/10",
};
const inputCls = "w-full rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E50914] transition-colors";

// Commercial business verification — required (server-enforced) before any
// property can be published. 6-digit code to the business email; free/public
// email domains route to admin review automatically.
export default function BusinessVerification() {
  const [v, setV] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.commercialVerification().then(setV).catch((e) => { toast.error(e.message); setV({ status: "not_started" }); });
  useEffect(() => { load(); }, []);

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.commercialVerificationStart({ business_name: name, business_email: email });
      setCode("");
      toast.success(r.email_sent ? "Verification code sent to your business email." : "Verification started — check with support if the email doesn't arrive.");
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.commercialVerificationConfirm(code.trim());
      toast.success(r.status === "approved" ? "Business verified — you can publish properties." : "Email verified — your business is now in admin review.");
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (!v) return <div className="card-dark h-36 animate-pulse" />;
  const status = v.status || "not_started";

  return (
    <div className="card-dark p-6" data-testid="business-verification">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <ShieldCheck className="w-4 h-4 text-[#FF3B45]" />
          <p className="meta-label !mb-0">Business verification</p>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-widest border rounded-full px-2.5 py-1 ${STATUS_CLS[status] || "text-zinc-400 border-zinc-600/40 bg-white/[0.03]"}`} data-testid="verification-status">
          {STATUS_LABEL[status] || status}
        </span>
      </div>

      {status === "approved" && (
        <p className="mt-3 text-sm text-zinc-300" data-testid="verification-approved-note">
          {v.business_name} · {v.business_email} — verified. You can publish commercial properties.
        </p>
      )}
      {(status === "admin_review_required" || status === "more_info_required") && (
        <div className="mt-3" data-testid="verification-review-note">
          <p className="text-sm text-zinc-300">{v.business_name} · {v.business_email} — email verified{status === "admin_review_required" ? ", awaiting admin review" : ", admin requested more information"}.</p>
          {v.review_note && <p className="mt-1.5 text-xs text-amber-300/90">Note from review: {v.review_note}</p>}
        </div>
      )}
      {status === "rejected" && (
        <div className="mt-3" data-testid="verification-rejected-note">
          <p className="text-sm text-red-300">Verification was rejected.{v.review_note ? ` Reason: ${v.review_note}` : ""} Update your details and resubmit below.</p>
        </div>
      )}

      {(status === "not_started" || status === "rejected" || status === "more_info_required") && (
        <form onSubmit={start} className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="verification-start-form">
          <input required minLength={2} maxLength={120} value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Business name (e.g. ABC Property Management Inc.)" className={inputCls} data-testid="verification-business-name" aria-label="Business name" />
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="Business email (e.g. admin@abcproperties.com)" className={inputCls} data-testid="verification-business-email" aria-label="Business email" />
          <div className="sm:col-span-2 flex items-center gap-3">
            <button type="submit" disabled={busy} className="btn-primary !py-2.5 text-xs disabled:opacity-50" data-testid="verification-start-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Send verification code"}
            </button>
            <p className="text-[11px] text-zinc-600">Free email providers (gmail, yahoo, outlook…) require admin review before publishing.</p>
          </div>
        </form>
      )}

      {status === "email_verification_required" && (
        <form onSubmit={confirm} className="mt-4" data-testid="verification-confirm-form">
          <p className="text-xs text-zinc-400 mb-2.5">Enter the 6-digit code sent to <span className="text-white">{v.business_email}</span>:</p>
          <div className="flex gap-2.5">
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                   inputMode="numeric" placeholder="000000" aria-label="Verification code" data-testid="verification-code"
                   className={`${inputCls} !w-40 text-center font-mono tracking-[0.3em]`} />
            <button type="submit" disabled={busy || code.length !== 6} className="btn-primary !py-2.5 text-xs disabled:opacity-50" data-testid="verification-confirm-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Verify"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
